import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
// SVG icon imports — kept for easy rollback
// import BuildIcon from '@material-ui/icons/Build';
// import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
// import HomeIcon from '@material-ui/icons/Home';
// import ScheduleIcon from '@material-ui/icons/Schedule';
// import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar'; // still used for driving row label
import markerSprite from '../../assets/marker_sprite.png';
import { consolidateTimelineEvents, normalizeLocationType } from './utils/timelineConsolidation';

// ── Colour palette (matches STATUS_CONFIG in TruckTrackingDashboard) ─────────
const COLORS = {
  JOBSITE: '#2E7D32',
  TRACKED_LOCATION: '#EF6C00',
  YARD: '#6D6D6D',
  IDLE_OTHER: '#C62828',
  DRIVING: '#1E88E5',
  CLOCK_IN: '#00897B',
  CLOCK_OUT: '#5E35B1',
  JOB_CHECK_IN: '#1565C0',
  JOB_CHECK_OUT: '#AD1457',
  SCHEDULED_ARRIVAL: '#455A64',
  SCHEDULED_DEPARTURE: '#78909C',
  WORK_TICKET_COMPLETED: '#6A1B9A',
};

const colorFor = (locationType) => COLORS[locationType] || '#607D8B';

// SVG icon map — kept for easy rollback
// const ICONS = {
//   JOBSITE: BuildIcon,
//   TRACKED_LOCATION: LocalGasStationIcon,
//   YARD: HomeIcon,
//   IDLE_OTHER: ScheduleIcon,
//   DRIVING: DirectionsCarIcon,
// };
// const iconFor = (locationType) => ICONS[locationType] || ScheduleIcon;

// ── Sprite icon positions ────────────────────────────────────────────────────
// Sprite is 1024×1024, 3 cols × 3 rows.  Each cell ≈ 341.3 × 341.3.
// We display each as a 24×24 square cropped to the pin area.
// background-position percentages: x = col/(cols-1)*100, y = row/(rows-1)*100
const SPRITE_POS = {
  DRIVING: '0% 0%',                   // row 0, col 0
  JOBSITE: '50% 0%',                  // row 0, col 1
  TRACKED_LOCATION: '100% 0%',        // row 0, col 2
  IDLE_OTHER: '0% 50%',               // row 1, col 0
  YARD: '50% 50%',                    // row 1, col 1
  SCHEDULED_ARRIVAL: '100% 50%',      // row 1, col 2
  SCHEDULED_DEPARTURE: '0% 100%',     // row 2, col 0
  WORK_TICKET_COMPLETED: '50% 100%',  // row 2, col 1
};
const DEFAULT_SPRITE_POS = '100% 100%'; // row 2, col 2 (grey default)
const SPRITE_MARKER_TYPES = new Set([
  'SCHEDULED_ARRIVAL',
  'SCHEDULED_DEPARTURE',
  'WORK_TICKET_COMPLETED',
]);

// ── Summary-bar key mapping from API summary fields to canonical types ────────
const SUMMARY_FIELDS = [
  { key: 'drivingMinutes', type: 'DRIVING', label: 'Driving' },
  { key: 'onSiteMinutes', type: 'JOBSITE', label: 'On-Site' },
  { key: 'trackedLocationMinutes', type: 'TRACKED_LOCATION', label: 'Tracked' },
  { key: 'idleOtherMinutes', type: 'IDLE_OTHER', label: 'Idle' },
  { key: 'atYardMinutes', type: 'YARD', label: 'Yard' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatMinutes = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return '--';
  const m = Math.round(Number(val));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
};

const formatTime = (val) => {
  if (!val) return '--';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatProximity = (stop) => {
  if (!stop) return null;

  const distanceFromCenter = Number(stop.distanceFromLocationMeters);
  if (!Number.isFinite(distanceFromCenter)) return null;

  const roundedDistance = Math.round(distanceFromCenter);
  const geofenceRadius = Number(stop.geofenceRadiusMeters);
  const distanceToGeofence = Number(stop.distanceToGeofenceMeters);

  if (!Number.isFinite(geofenceRadius) || !Number.isFinite(distanceToGeofence)) {
    return `${roundedDistance}m from center`;
  }

  const boundaryDelta = Math.round(Math.abs(distanceToGeofence));
  const boundaryText = distanceToGeofence >= 0
    ? `${boundaryDelta}m inside geofence`
    : `${boundaryDelta}m outside geofence`;

  return `${roundedDistance}m from center (${boundaryText})`;
};

const TYPE_LABELS = {
  JOBSITE: 'Jobsite',
  TRACKED_LOCATION: 'Tracked Location',
  YARD: 'Yard',
  IDLE_OTHER: 'Idle / Other',
  CLOCK_IN: 'Clock-In',
  CLOCK_OUT: 'Clock-Out',
  JOB_CHECK_IN: 'Job Check-In',
  JOB_CHECK_OUT: 'Job Check-Out',
  SCHEDULED_ARRIVAL: 'Scheduled Arrival',
  SCHEDULED_DEPARTURE: 'Scheduled Departure',
  WORK_TICKET_COMPLETED: 'Work Ticket Completed',
};

const SOURCE_LABELS = {
  VEHICLE_VISIT: 'Visits',
  CAMPUS_EVENT: 'Yard Event',
  HISTORY_SEGMENT: 'History',
  GEOTAB_TRIP_API: 'Trip API',
  GEOTAB_TRIP_API_CACHE: 'Trip Cache',
  IDLING_EVENT: 'Idling',
  ASPIRE_CLOCK_TIMES: 'Aspire Clock',
  ASPIRE_WORK_TICKET_TIMES: 'Aspire Ticket',
  ASPIRE_DAY_CACHE: 'Aspire Cache',
  DAILY_ROUTE: 'Daily Route',
  ASPIRE_WORK_TICKET_LOCAL: 'Work Ticket Local',
};

const sourceLabel = (value) => {
  if (!value) return 'Unknown Source';
  return SOURCE_LABELS[value] || value;
};

// ── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {},
  compactRoot: {
    maxHeight: 280,
    overflowY: 'auto',
  },

  /* Summary bar */
  summaryBar: {
    display: 'flex',
    height: 14,
    borderRadius: 7,
    overflow: 'hidden',
    marginBottom: 6,
  },
  summarySegment: {
    transition: 'width 0.3s ease',
    minWidth: 2,
  },
  summaryLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
  thresholdBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
  filterHint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },

  /* Vertical timeline */
  timeline: {
    position: 'relative',
    paddingLeft: 72,
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: 42,
    paddingBottom: theme.spacing(0.5),
    cursor: 'pointer',
    borderRadius: 4,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  rowHighlighted: {
    backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
  },
  timeColumn: {
    position: 'absolute',
    left: -72,
    width: 62,
    textAlign: 'right',
    paddingTop: 2,
    fontSize: 11,
    color: theme.palette.text.secondary,
    lineHeight: '18px',
  },
  lineTrack: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: theme.palette.divider,
  },

  /* Stop dot — sprite-based pin icon */
  dot: {
    position: 'absolute',
    left: -12,
    top: 0,
    width: 24,
    height: 24,
    zIndex: 1,
    backgroundImage: `url(${markerSprite})`,
    backgroundSize: '300% 300%', // 3 cols × 3 rows
    backgroundRepeat: 'no-repeat',
    imageRendering: 'auto',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
  },
  markerSpriteDot: {
    position: 'absolute',
    left: -12,
    top: 0,
    width: 24,
    height: 24,
    zIndex: 2,
    backgroundImage: `url(${markerSprite})`,
    backgroundSize: '300% 300%', // 3 cols × 3 rows
    backgroundRepeat: 'no-repeat',
    imageRendering: 'auto',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
  },
  dotPulsing: {
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { boxShadow: '0 0 0 0 rgba(46,125,50,0.5)' },
    '50%': { boxShadow: '0 0 0 6px rgba(46,125,50,0)' },
  },
  stopNumber: {
    position: 'absolute',
    left: -12,
    top: -6,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#fff',
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
    zIndex: 2,
  },

  /* Stop details */
  stopContent: {
    paddingLeft: theme.spacing(2),
    flex: 1,
    minWidth: 0,
  },
  stopName: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    wordBreak: 'break-word',
  },
  stopMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 2,
  },
  durationChip: {
    height: 20,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
  },
  typeLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  activeLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#2E7D32',
  },
  proximityLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  markerDot: {
    position: 'absolute',
    left: -9,
    top: 2,
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid #fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    zIndex: 2,
  },
  markerContent: {
    paddingLeft: theme.spacing(2),
    flex: 1,
    minWidth: 0,
  },
  markerName: {
    fontWeight: 600,
    fontSize: 12,
    lineHeight: '18px',
    wordBreak: 'break-word',
  },
  markerMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 2,
  },
  markerTypeLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  sourceChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 9,
  },
  provenanceLabel: {
    fontSize: 10,
    color: theme.palette.text.secondary,
  },

  /* Driving segment */
  drivingRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    minHeight: 28,
    paddingBottom: theme.spacing(0.25),
  },
  drivingLine: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
    borderLeft: `2px dashed ${COLORS.DRIVING}`,
  },
  drivingLabel: {
    paddingLeft: theme.spacing(2),
    fontSize: 12,
    fontStyle: 'italic',
    color: COLORS.DRIVING,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& svg': { fontSize: 14 },
  },

  /* Expected schedule */
  expectedTitle: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  expectedRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: 36,
    paddingBottom: theme.spacing(0.25),
  },
  expectedDot: {
    position: 'absolute',
    left: -7,
    top: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2px dashed',
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  expectedContent: {
    paddingLeft: theme.spacing(2),
    flex: 1,
  },
  expectedName: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  expectedTime: {
    fontSize: 11,
    color: theme.palette.text.disabled,
  },
}));

// ── Component ────────────────────────────────────────────────────────────────
const VisualTimeline = ({ events, summary, expectedSchedule, compact, onResync, resyncLoading, onStopsChange, onEntryClick, highlightedStopIdx }) => {
  const classes = useStyles();
  const [filterMode, setFilterMode] = useState('ALL');
  const [flaggedThresholdMinutes, setFlaggedThresholdMinutes] = useState(20);

  const consolidated = useMemo(
    () => consolidateTimelineEvents(events || []),
    [events],
  );

  const filteredConsolidated = useMemo(() => {
    if (filterMode === 'ALL') return consolidated;

    return consolidated.filter((entry) => {
      if (filterMode === 'ASPIRE') return entry.kind === 'MARKER';

      if (filterMode === 'OPERATIONAL') {
        if (entry.kind === 'DRIVING') return true;
        if (entry.kind !== 'STOP') return false;
        const type = normalizeLocationType(entry.stop?.locationType);
        return type === 'JOBSITE' || type === 'YARD';
      }

      if (filterMode === 'IDLING') {
        if (entry.kind === 'MARKER') return true;
        if (entry.kind !== 'STOP') return false;
        const type = normalizeLocationType(entry.stop?.locationType);
        return type === 'IDLE_OTHER' || Boolean(entry.stop?.hasIdlingEvidence);
      }

      if (filterMode === 'FLAGGED') {
        if (entry.kind !== 'STOP') return false;
        const type = normalizeLocationType(entry.stop?.locationType);
        const duration = Number(entry.durationMinutes || entry.stop?.durationMinutes || 0);
        const isFlagType = type === 'TRACKED_LOCATION' || type === 'IDLE_OTHER';
        return isFlagType && duration >= flaggedThresholdMinutes;
      }

      return true;
    });
  }, [consolidated, filterMode, flaggedThresholdMinutes]);

  // ── Assign stop numbers (STOP entries only, skip DRIVING) ──────────
  // Filtered numbering is used in the visible timeline list.
  const stopNumberMap = useMemo(() => {
    const map = new Map();
    let counter = 0;
    filteredConsolidated.forEach((entry, idx) => {
      if (entry.kind === 'STOP') {
        counter += 1;
        map.set(idx, counter);
      }
    });
    return map;
  }, [filteredConsolidated]);

  // Full-day numbering is used by the map overlay so filters do not hide context.
  const allStopNumberMap = useMemo(() => {
    const map = new Map();
    let counter = 0;
    consolidated.forEach((entry, idx) => {
      if (entry.kind === 'STOP') {
        counter += 1;
        map.set(idx, counter);
      }
    });
    return map;
  }, [consolidated]);

  // Build full stop/marker list for map overlay.
  const computedStops = useMemo(() => {
    const stops = [];
    consolidated.forEach((entry, idx) => {
      if (entry.kind === 'STOP') {
        const num = allStopNumberMap.get(idx);
        if (!num) return;
        stops.push({
          stopNumber: num,
          lat: entry.stop?.lat ?? null,
          lon: entry.stop?.lon ?? null,
          displayName: entry.stop?.displayName || entry.stop?.locationName || 'Unknown',
          locationType: entry.stop?.locationType || 'OTHER',
          isMarker: false,
        });
        return;
      }

      if (entry.kind === 'MARKER') {
        stops.push({
          stopNumber: null,
          lat: entry.marker?.lat ?? null,
          lon: entry.marker?.lon ?? null,
          displayName: entry.marker?.locationName || 'Aspire Event',
          locationType: entry.marker?.markerType || 'ASPIRE_EVENT',
          markerType: entry.marker?.markerType || 'ASPIRE_EVENT',
          isMarker: true,
        });
      }
    });
    return stops;
  }, [consolidated, allStopNumberMap]);

  // Map each rendered entry object to its corresponding map marker index.
  const entryToStopIndex = useMemo(() => {
    const map = new Map();
    let stopIdx = -1;
    consolidated.forEach((entry) => {
      if (entry.kind === 'STOP' || entry.kind === 'MARKER') {
        stopIdx += 1;
        map.set(entry, stopIdx);
      }
    });
    return map;
  }, [consolidated]);

  const prevStopsKeyRef = useRef('');
  useEffect(() => {
    if (!onStopsChange) return;
    const key = JSON.stringify(computedStops);
    if (key === prevStopsKeyRef.current) return;
    prevStopsKeyRef.current = key;
    onStopsChange(computedStops);
  }, [computedStops, onStopsChange]);

  // ── Summary bar ────────────────────────────────────────────────────────
  const summarySegments = useMemo(() => {
    if (!summary) return [];
    const segments = SUMMARY_FIELDS.map(({ key, type, label }) => ({
      type,
      label,
      minutes: Math.max(0, Number(summary[key]) || 0),
      color: COLORS[type],
    })).filter((s) => s.minutes > 0);

    const total = segments.reduce((acc, s) => acc + s.minutes, 0);
    return segments.map((s) => ({ ...s, pct: total > 0 ? (s.minutes / total) * 100 : 0 }));
  }, [summary]);

  const hasSummary = summarySegments.length > 0;
  const totalEntries = consolidated.length;
  const filteredEntryCount = filteredConsolidated.length;
  const hasEntries = filteredConsolidated.length > 0;
  const hasExpected = expectedSchedule?.stops?.length > 0;

  if (!hasSummary && !hasEntries && !hasExpected) {
    return (
      <Box py={2} textAlign="center">
        <Typography variant="body2" color="textSecondary">
          No events for this date.
        </Typography>
        {onResync && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={onResync}
            disabled={resyncLoading}
            startIcon={resyncLoading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
            style={{ marginTop: 12 }}
          >
            {resyncLoading ? 'Re-syncing...' : 'Re-sync day data'}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box className={compact ? classes.compactRoot : classes.root}>
      {/* Summary bar */}
      {hasSummary && (
        <>
          <Box className={classes.summaryBar}>
            {summarySegments.map((seg) => (
              <Box
                key={seg.type}
                className={classes.summarySegment}
                style={{ width: `${seg.pct}%`, backgroundColor: seg.color }}
                title={`${seg.label}: ${formatMinutes(seg.minutes)}`}
              />
            ))}
          </Box>
          <Box className={classes.summaryLegend}>
            {summarySegments.map((seg) => (
              <span key={seg.type} className={classes.legendItem}>
                <span className={classes.legendDot} style={{ backgroundColor: seg.color }} />
                {seg.label} {formatMinutes(seg.minutes)}
              </span>
            ))}
          </Box>
        </>
      )}

      {!compact && totalEntries > 0 && (
        <>
          <Box className={classes.filterBar}>
            {['ALL', 'OPERATIONAL', 'ASPIRE', 'FLAGGED', 'IDLING'].map((mode) => (
              <Button
                key={mode}
                size="small"
                variant={filterMode === mode ? 'contained' : 'outlined'}
                color={filterMode === mode ? 'primary' : 'default'}
                onClick={() => setFilterMode(mode)}
              >
                {mode === 'ALL' ? 'All' : mode.charAt(0) + mode.slice(1).toLowerCase()}
              </Button>
            ))}
          </Box>
          {filterMode === 'FLAGGED' && (
            <Box className={classes.thresholdBar}>
              <Typography className={classes.filterHint}>Flag Threshold:</Typography>
              {[10, 20, 30, 45].map((minutes) => (
                <Button
                  key={minutes}
                  size="small"
                  variant={flaggedThresholdMinutes === minutes ? 'contained' : 'outlined'}
                  color={flaggedThresholdMinutes === minutes ? 'secondary' : 'default'}
                  onClick={() => setFlaggedThresholdMinutes(minutes)}
                >
                  {minutes}m+
                </Button>
              ))}
            </Box>
          )}
          <Typography className={classes.filterHint}>
            Showing {filteredEntryCount} of {totalEntries} timeline entries
          </Typography>
        </>
      )}

      {/* Vertical timeline */}
      {hasEntries && (
        <Box className={classes.timeline}>
          {(() => {
            return filteredConsolidated.map((entry, idx) => {
            if (entry.kind === 'DRIVING') {
              return (
                <Box key={`dr-${idx}`} className={classes.drivingRow}>
                  <Box className={classes.drivingLine} />
                  <span className={classes.drivingLabel}>
                    <DirectionsCarIcon />
                    driving {formatMinutes(entry.durationMinutes)}
                  </span>
                </Box>
              );
            }

            // Both STOP and MARKER entries map to a timelineStops entry
            const myStopIdx = entryToStopIndex.get(entry);
            const isHighlighted = myStopIdx != null && highlightedStopIdx === myStopIdx;
            const handleClick = (onEntryClick && myStopIdx != null)
              ? () => onEntryClick(myStopIdx)
              : undefined;

            if (entry.kind === 'MARKER') {
              const markerType = normalizeLocationType(entry.marker?.markerType || 'ASPIRE_EVENT');
              const markerColor = colorFor(markerType);
              const markerSpritePos = SPRITE_POS[markerType] || DEFAULT_SPRITE_POS;
              const useSpriteMarker = SPRITE_MARKER_TYPES.has(markerType);

              return (
                <Box
                  key={`mk-${idx}`}
                  className={`${classes.row} ${isHighlighted ? classes.rowHighlighted : ''}`}
                  onClick={handleClick}
                >
                  <Box className={classes.lineTrack} />
                  <span className={classes.timeColumn}>{formatTime(entry.time)}</span>
                  {useSpriteMarker ? (
                    <span className={classes.markerSpriteDot} style={{ backgroundPosition: markerSpritePos }} />
                  ) : (
                    <span className={classes.markerDot} style={{ backgroundColor: markerColor }} />
                  )}
                  <Box className={classes.markerContent}>
                    <Typography className={classes.markerName}>
                      {entry.marker?.locationName || 'Aspire Event'}
                    </Typography>
                    <Box className={classes.markerMeta}>
                      <span className={classes.markerTypeLabel}>
                        {TYPE_LABELS[markerType] || markerType}
                      </span>
                      {entry.marker?.source ? (
                        <Chip
                          className={classes.sourceChip}
                          label={sourceLabel(entry.marker.source)}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                      {entry.marker?.matchedBy ? (
                        <Tooltip
                          title={`match: ${entry.marker.matchedBy}${entry.marker.contactId ? ` | contact ${entry.marker.contactId}` : ''}${entry.marker.employeeId ? ` | employee ${entry.marker.employeeId}` : ''}`}
                        >
                          <span className={classes.provenanceLabel}>match info</span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              );
            }

            // STOP
            const stop = entry.stop;
            const locType = normalizeLocationType(stop.locationType);
            const color = colorFor(locType);
            const spritePos = SPRITE_POS[locType] || DEFAULT_SPRITE_POS;
            const proximityText = formatProximity(stop);
            const thisStopNumber = stopNumberMap.get(idx);

            return (
              <Box
                key={`st-${idx}`}
                className={`${classes.row} ${isHighlighted ? classes.rowHighlighted : ''}`}
                onClick={handleClick}
              >
                <Box className={classes.lineTrack} />
                <span className={classes.timeColumn}>
                  {formatTime(entry.startTime)}
                  {entry.endTime ? (
                    <>
                      <br />
                      {formatTime(entry.endTime)}
                    </>
                  ) : null}
                </span>
                <span
                  className={`${classes.dot} ${stop.isActive ? classes.dotPulsing : ''}`}
                  style={{ backgroundPosition: spritePos }}
                />
                {thisStopNumber && (
                  <span
                    className={classes.stopNumber}
                    style={{ borderColor: color, color }}
                  >
                    {thisStopNumber}
                  </span>
                )}
                <Box className={classes.stopContent}>
                  <Typography className={classes.stopName}>
                    {stop.displayName || stop.locationName || 'Unknown'}
                  </Typography>
                  <Box className={classes.stopMeta}>
                    {entry.durationMinutes != null && (
                      <Chip
                        className={classes.durationChip}
                        label={formatMinutes(entry.durationMinutes)}
                        size="small"
                        style={{ backgroundColor: color }}
                      />
                    )}
                    <span className={classes.typeLabel}>
                      {TYPE_LABELS[locType] || locType}
                    </span>
                    {Array.isArray(stop.sources) && stop.sources.length > 0
                      ? stop.sources.map((source) => (
                        <Chip
                          key={`${entry.startTime}-${source}`}
                          className={classes.sourceChip}
                          label={sourceLabel(source)}
                          size="small"
                          variant="outlined"
                        />
                      ))
                      : null}
                    {stop.hasIdlingEvidence ? (
                      <Chip
                        className={classes.sourceChip}
                        label={`Idling ${stop.idlingDurationMinutes ? `${formatMinutes(stop.idlingDurationMinutes)}` : ''}`}
                        size="small"
                        style={{ backgroundColor: '#FFF8E1', borderColor: '#FFB300' }}
                        variant="outlined"
                      />
                    ) : null}
                    {proximityText ? (
                      <span className={classes.proximityLabel}>{proximityText}</span>
                    ) : null}
                    {stop.isActive && (
                      <span className={classes.activeLabel}>Currently here</span>
                    )}
                  </Box>
                </Box>
              </Box>
            );
            });
          })()}
        </Box>
      )}

      {!hasEntries && totalEntries > 0 ? (
        <Box py={2}>
          <Typography variant="body2" color="textSecondary">
            No timeline entries match the current filters.
          </Typography>
        </Box>
      ) : null}

      {/* Expected schedule */}
      {hasExpected && (
        <>
          <Typography variant="subtitle2" className={classes.expectedTitle}>
            Expected Schedule
          </Typography>
          <Box className={classes.timeline}>
            {expectedSchedule.stops.map((stop, idx) => (
              <Box key={`exp-${idx}`} className={classes.expectedRow}>
                <Box className={classes.lineTrack} style={{ borderStyle: 'dashed' }} />
                <span
                  className={classes.expectedDot}
                  style={{ borderColor: '#90A4AE' }}
                />
                <Box className={classes.expectedContent}>
                  <Typography className={classes.expectedName}>
                    {stop.order != null ? `${stop.order}. ` : ''}{stop.name}
                  </Typography>
                  <Typography className={classes.expectedTime}>
                    {stop.arrivalTime || '--'} to {stop.departureTime || '--'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default VisualTimeline;
